class TabSection extends HTMLElement {
    constructor() {
        super();
        this.tabs = this.querySelectorAll('[data-tab]');
        this.tabPanels = this.querySelectorAll('[data-tab-panel]');
        this.boundHandlers = new Map();
    }

    connectedCallback() {
        if (this.tabs.length === 0 || this.tabPanels.length === 0) {
            console.warn('TabSection: No tabs or panels found');
            return;
        }

        this.tabs.forEach(tab => {
            const clickHandler = (e) => {
                e.preventDefault();
                this.activateTab(tab);
                const tabName = tab.dataset.tabName;
                if (tabName) {
                    this.activeTabPanel(tabName);
                }
            };

            const keyHandler = (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    clickHandler(e);
                }
            };

            this.boundHandlers.set(tab, { click: clickHandler, keydown: keyHandler });
            
            tab.addEventListener('click', clickHandler);
            tab.addEventListener('keydown', keyHandler);
            
            // Accessibility improvements
            tab.setAttribute('role', 'tab');
            tab.setAttribute('tabindex', '0');
            if (!tab.hasAttribute('aria-label') && tab.textContent) {
                tab.setAttribute('aria-label', tab.textContent.trim());
            }
        });

        // Set ARIA attributes for panels
        this.tabPanels.forEach(panel => {
            panel.setAttribute('role', 'tabpanel');
            if (panel.dataset.tabName) {
                panel.setAttribute('aria-labelledby', `tab-${panel.dataset.tabName}`);
            }
        });
    }

    disconnectedCallback() {
        // Clean up event listeners to prevent memory leaks
        this.tabs.forEach(tab => {
            const handlers = this.boundHandlers.get(tab);
            if (handlers) {
                tab.removeEventListener('click', handlers.click);
                tab.removeEventListener('keydown', handlers.keydown);
            }
        });
        this.boundHandlers.clear();
    }

    activateTab(tab) {
        if (!tab) return;
        
        this.tabs.forEach(t => {
            t.classList.remove('active');
            t.setAttribute('aria-selected', 'false');
        });
        
        tab.classList.add('active');
        tab.setAttribute('aria-selected', 'true');
    }

    activeTabPanel(tabName) {
        if (!tabName) return;
        
        this.tabPanels.forEach(panel => {
            if (panel.dataset.tabName === tabName) {
                panel.classList.add('active');
                panel.setAttribute('aria-hidden', 'false');
            } else {
                panel.classList.remove('active');
                panel.setAttribute('aria-hidden', 'true');
            }
        });
    }
}
customElements.define('tab-section', TabSection);

class DiscoveryQuizPopup extends HTMLElement {
    constructor() {
        super();
        this.activeIndex = 0;
        this.discoveryQuizBtn = null;
        this.quizQuestions = [];
        this.closeBtn = null;
        this.nextStepBtn = null;
        this.boundHandlers = {};
        this.documentClickHandler = null;
        this.userAnswers = new Map();
        this.loadingElement = null;
        this.resultsContainer = null;
    }

    connectedCallback() {
        // Initialize elements with error checking
        this.discoveryQuizBtn = document.querySelector('#discovery-quiz');
        this.quizQuestions = this.querySelectorAll('[data-quiz-questions-container]');
        this.closeBtn = this.querySelector('[data-close-btn]');
        this.nextStepBtn = this.querySelector('[data-next-step]');
        this.loadingElement = this.querySelector('.loading-recommendations');
        this.resultsContainer = this.querySelector('#quiz-results');

        if (this.quizQuestions.length === 0) {
            console.warn('DiscoveryQuizPopup: No quiz questions found');
            return;
        }

        this.setupEventListeners();
        this.setupAccessibility();
        this.setupOptionListeners();
        this.updateQuestionVisibility(); // Initialize first question
        this.initializeProgressBars();
        this.updateProgressBar();
        this.updateButtonState('next'); // Initialize button state
    }

    disconnectedCallback() {
        // Clean up all event listeners
        if (this.discoveryQuizBtn && this.boundHandlers.discoveryQuizClick) {
            this.discoveryQuizBtn.removeEventListener('click', this.boundHandlers.discoveryQuizClick);
        }
        
        if (this.documentClickHandler) {
            document.removeEventListener('click', this.documentClickHandler);
        }
        
        if (this.closeBtn && this.boundHandlers.closeBtnClick) {
            this.closeBtn.removeEventListener('click', this.boundHandlers.closeBtnClick);
        }
        
        if (this.nextStepBtn && this.boundHandlers.nextStepClick) {
            this.nextStepBtn.removeEventListener('click', this.boundHandlers.nextStepClick);
        }

        // Remove escape key listener
        if (this.boundHandlers.escapeHandler) {
            document.removeEventListener('keydown', this.boundHandlers.escapeHandler);
        }
    }

    setupEventListeners() {
        // Discovery quiz button handler
        if (this.discoveryQuizBtn) {
            this.boundHandlers.discoveryQuizClick = (e) => {
                e.preventDefault();
                this.showPopup();
            };
            this.discoveryQuizBtn.addEventListener('click', this.boundHandlers.discoveryQuizClick);
        }

        // Document click handler for drawer links and footer
        this.documentClickHandler = (e) => {
            const link = e.target.closest('#HeaderDrawer-discovery-quiz, a[href="#discovery-quiz"], [data-footer-discovery-quiz]');
            if (link) {
                e.preventDefault();
                e.stopPropagation();
                this.showPopup();
            }
        };
        document.addEventListener('click', this.documentClickHandler);

        // Close button handler
        if (this.closeBtn) {
            this.boundHandlers.closeBtnClick = (e) => {
                e.preventDefault();
                this.hidePopup();
            };
            this.closeBtn.addEventListener('click', this.boundHandlers.closeBtnClick);
        }

        // Next step button handler
        if (this.nextStepBtn) {
            this.boundHandlers.nextStepClick = (e) => {
                e.preventDefault();
                
                console.log('Button clicked, disabled state:', this.nextStepBtn.disabled, 'has disabled class:', this.nextStepBtn.classList.contains('disabled'));
                
                // Don't proceed if button is disabled
                if (this.nextStepBtn.disabled || this.nextStepBtn.classList.contains('disabled')) {
                    console.log('Button click blocked - button is disabled');
                    return;
                }
                
                console.log('Proceeding with handleNextStep');
                this.handleNextStep();
            };
            this.nextStepBtn.addEventListener('click', this.boundHandlers.nextStepClick);
        }

        // Escape key handler
        this.boundHandlers.escapeHandler = (e) => {
            if (e.key === 'Escape' && this.style.display !== 'none') {
                this.hidePopup();
            }
        };
        document.addEventListener('keydown', this.boundHandlers.escapeHandler);
    }

    setupAccessibility() {
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.setAttribute('aria-labelledby', 'quiz-title');
        
        if (this.closeBtn) {
            this.closeBtn.setAttribute('aria-label', 'Close quiz');
        }
    }

    setupOptionListeners() {
        // Add click handlers for all radio button options
        this.quizQuestions.forEach(question => {
            const radioInputs = question.querySelectorAll('input[type="radio"]');
            const labels = question.querySelectorAll('label');
            
            radioInputs.forEach(input => {
                input.addEventListener('change', () => {
                    // Clear validation message when option is selected
                    this.removeValidationMessage();
                    
                    // Update visual state
                    this.updateOptionSelection(question);
                });
            });
        });
    }

    updateOptionSelection(questionContainer) { 
        const labels = questionContainer.querySelectorAll('label');
        const inputs = questionContainer.querySelectorAll('input[type="radio"]');
        
        labels.forEach((label, index) => {
            const input = inputs[index];
            if (input && input.checked) {
                label.style.transform = 'translateY(-2px)';
                label.style.boxShadow = '0 4px 8px rgba(0,0,0,0.1)';
            } else {
                label.style.transform = '';
                label.style.boxShadow = '';
            }
        });
    }

    removeValidationMessage() {
        const validationMessage = this.querySelector('.validation-message');
        if (validationMessage) {
            validationMessage.remove();
        }
    }

    handleNextStep() {
        try {
            // Check if max index is reached (on results screen)
            if (this.activeIndex >= this.quizQuestions.length - 1) {
                this.resetQuiz();
                return;
            }

            // Check if a radio button is selected for current question
            const currentQuestion = this.quizQuestions[this.activeIndex];
            if (!currentQuestion) return;

            const selectedOption = currentQuestion.querySelector('input[type="radio"]:checked');
            if (!selectedOption) {
                this.showValidationMessage('Please select an option before proceeding.');
                return;
            }

            // Store the answer
            const questionIndex = selectedOption.getAttribute('data-question');
            this.userAnswers.set(questionIndex, {
                value: selectedOption.value,
                element: selectedOption
            });

            // Check if this is the last question
            const isLastQuestion = this.activeIndex === this.quizQuestions.length - 2;

            if (isLastQuestion) {
                this.showRecommendations();
                this.updateButtonState('restart');
            } else {
                this.showNextQuestion();
            }

            this.updateProgressBar();
        } catch (error) {
            console.error('DiscoveryQuizPopup: Error in handleNextStep', error);
        }
    }

    showValidationMessage(message) {
        // Remove any existing message first
        this.removeValidationMessage();

        const messageEl = document.createElement('div');
        messageEl.className = 'validation-message';
        messageEl.textContent = message;
        
        const currentQuestion = this.quizQuestions[this.activeIndex];
        if (currentQuestion) {
            const optionsContainer = currentQuestion.querySelector('.quiz-options');
            if (optionsContainer) {
                optionsContainer.appendChild(messageEl);
            } else {
                currentQuestion.appendChild(messageEl);
            }
            
            // Auto-remove after 4 seconds
            setTimeout(() => this.removeValidationMessage(), 4000);
        }
    }

    resetQuiz() {
        this.activeIndex = 0;
        this.userAnswers.clear();
        
        this.updateButtonState('next');
        
        // Clear all selections
        this.quizQuestions.forEach(question => {
            const inputs = question.querySelectorAll('input[type="radio"]');
            inputs.forEach(input => input.checked = false);
        });
        
        // Clear results
        if (this.resultsContainer) {
            this.resultsContainer.innerHTML = '';
        }
        
        // Reset progress bars
        this.initializeProgressBars();
        
        this.updateQuestionVisibility();
        this.updateProgressBar();
    }

    showPopup() {
        this.style.display = 'block';
        document.body.style.overflow = 'hidden';
        
        // Focus management for accessibility
        const firstFocusable = this.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
        if (firstFocusable) {
            firstFocusable.focus();
        }
    }

    hidePopup() {
        this.style.display = 'none';
        document.body.style.overflow = 'auto';
        
        // Return focus to trigger element
        if (this.discoveryQuizBtn) {
            this.discoveryQuizBtn.focus();
        }
    }

    showNextQuestion() {
        if (this.activeIndex < this.quizQuestions.length - 1) {
            this.activeIndex++;
            this.updateQuestionVisibility();
        }
    }

    updateQuestionVisibility() {
        this.quizQuestions.forEach((container, index) => {
            if (index === this.activeIndex) {
                container.classList.add('active');
                container.setAttribute('aria-hidden', 'false');
            } else {
                container.classList.remove('active');
                container.setAttribute('aria-hidden', 'true');
            }
        });

        // Remove any existing validation messages
        this.removeValidationMessage();
    }

    initializeProgressBars() {
        // Initialize all progress bars to start at proper width
        this.quizQuestions.forEach((question, index) => {
            const progressBar = question.querySelector('[data-progress-bar]');
            if (progressBar) {
                console.log(`Found progress bar in question ${index}`);
                // Set initial width based on question position
                if (index < this.quizQuestions.length - 1) { // Skip results container
                    progressBar.style.setProperty('width', '0%', 'important');
                }
            } else {
                console.log(`No progress bar found in question ${index}`);
            }
        });
    }

    updateProgressBar() {
        // Calculate progress based on current question number (excluding results screen)
        const totalQuestions = this.quizQuestions.length - 1; // Exclude results container
        const currentQuestionNum = Math.min(this.activeIndex + 1, totalQuestions);
        const progress = (currentQuestionNum / totalQuestions) * 100;
        
        console.log(`Progress update: Question ${currentQuestionNum} of ${totalQuestions}, Progress: ${progress}%`);
        
        // Update the progress bar in the currently active question
        const currentQuestion = this.quizQuestions[this.activeIndex];
        if (currentQuestion) {
            const progressBar = currentQuestion.querySelector('[data-progress-bar]');
            if (progressBar) {
                // Force the style update and make sure it overrides any existing styles
                progressBar.style.setProperty('width', `${Math.min(progress, 100)}%`, 'important');
                console.log('Progress bar updated successfully');
            } else {
                console.log('No progress bar found in current question');
            }
        } else {
            console.log('No current question found for index:', this.activeIndex);
        }
    }

    updateButtonState(state) {
        if (!this.nextStepBtn) return;

        console.log(`Button state update: ${state}`);

        if (state === 'next') {
            this.nextStepBtn.textContent = 'Next Step';
            this.nextStepBtn.disabled = false;
            this.nextStepBtn.classList.remove('disabled', 'start-again');
            this.nextStepBtn.style.pointerEvents = '';
            this.nextStepBtn.style.opacity = '';
        } else if (state === 'restart') {
            this.nextStepBtn.textContent = 'Start Again';
            this.nextStepBtn.disabled = false;
            this.nextStepBtn.classList.remove('disabled');
            this.nextStepBtn.classList.add('start-again');
            this.nextStepBtn.style.pointerEvents = '';
            this.nextStepBtn.style.opacity = '';
        } else if (state === 'disabled') {
            this.nextStepBtn.disabled = true;
            this.nextStepBtn.classList.add('disabled');
            this.nextStepBtn.classList.remove('start-again');
            this.nextStepBtn.style.pointerEvents = 'none';
            this.nextStepBtn.style.opacity = '0.5';
        }
    }

    async showRecommendations() {
        console.log('showRecommendations called');
        this.activeIndex++;
        this.updateQuestionVisibility();

        if (this.loadingElement) {
            console.log('Showing loading element');
            this.loadingElement.style.display = 'block';
        } else {
            console.log('Loading element not found');
        }

        // Simulate loading delay for better UX
        setTimeout(() => {
            console.log('Loading timeout completed, generating recommendations');
            try {
                this.generateRecommendations();
                if (this.loadingElement) {
                    this.loadingElement.style.display = 'none';
                    console.log('Loading element hidden');
                }
            } catch (error) {
                console.error('Error generating recommendations:', error);
                if (this.loadingElement) {
                    this.loadingElement.style.display = 'none';
                }
            }
        }, 1000);
    }

    generateRecommendations() {
        console.log('generateRecommendations called');
        if (!this.resultsContainer) {
            console.error('Results container not found');
            return;
        }

        // Get recommendations using the dynamic answer key system
        console.log('Calling calculateRecommendations...');
        const recommendations = this.calculateRecommendations();
        console.log('Recommendations calculated:', recommendations);
        
        let html = '';
        
        // Check if we have a random fallback (no matches)
        const hasRandomFallback = recommendations.some(rec => rec.isRandomFallback);
        
        if (hasRandomFallback) {
            html += `
                <div class="no-match-message">
                    <h6>Sorry, we didn't find a perfect match!</h6>
                    <p>But don't worry - we recommend this popular product that many customers love:</p>
                </div>
            `;
        } else {
            html += `<p>Based on your answers, we recommend this product for you:</p>`;
        }
        
        if (recommendations.length > 0) {
            html += `<div class="recommended-products">`;
            recommendations.forEach(product => {
                html += `
                    <div class="recommended-product">
                        ${product.image_url ? `<div class="product-image">
                            <img src="${this.buildImageUrl(product.image_url, product.shop_url)}" alt="${product.name}" loading="lazy" />
                        </div>` : ''}
                        <div class="product-info">
                            <h5>${product.name}</h5>
                            <p>${product.description}</p>
                            ${product.url ? `<a href="${product.url}" class="product-link button" target="_blank">View Product</a>` : ''}
                        </div>
                    </div>
                `;
            });
            html += `</div>`;
        }

        console.log('Setting results HTML:', html);
        this.resultsContainer.innerHTML = html;
        console.log('Results HTML set successfully');
    }

    calculateRecommendations() {
        // Use dynamic answer key system from window.discovery_quiz_key
        let recommendations = [];
        const userAnswers = Array.from(this.userAnswers.values()).map(answer => answer.value);
        
        console.log('User answers:', userAnswers);
        console.log('Available answer keys:', window.discovery_quiz_key);

        if (window.discovery_quiz_key && window.discovery_quiz_key.length > 0) {
            // Find matching answer keys
            window.discovery_quiz_key.forEach(answerKey => {
                const matchCount = this.countMatches(userAnswers, answerKey.key);
                
                if (matchCount > 0) {
                    console.log(`Match found: ${matchCount} matches for key:`, answerKey.key);
                    recommendations.push({
                        name: answerKey.products.title,
                        description: answerKey.description,
                        image_url: answerKey.products.image_url,
                        url: answerKey.products.url,
                        shop_url: answerKey.products.shop_url,
                        matchCount: matchCount
                    });
                }
            });

            // Sort by match count (highest first), keep ALL tied top-scoring matches
            recommendations.sort((a, b) => b.matchCount - a.matchCount);
            if (recommendations.length > 0) {
                const topScore = recommendations[0].matchCount;
                recommendations = recommendations.filter(rec => rec.matchCount === topScore);
            }
        }

        // Fallback recommendations if no matches found
        if (recommendations.length === 0 && window.discovery_quiz_key && window.discovery_quiz_key.length > 0) {
            // Pick a random product from available keys
            const randomKey = window.discovery_quiz_key[Math.floor(Math.random() * window.discovery_quiz_key.length)];
            recommendations.push({
                name: randomKey.products.title,
                description: randomKey.description,
                image_url: randomKey.products.image_url,
                url: randomKey.products.url,
                shop_url: randomKey.products.shop_url,
                isRandomFallback: true
            });
        } else if (recommendations.length === 0) {
            // Ultimate fallback if no keys available
            recommendations.push({
                name: 'Explore Our Full Range',
                description: 'We recommend browsing our complete skincare collection to find products that suit your needs.',
                url: '/collections/all',
                shop_url: window.location.origin,
                isRandomFallback: true
            });
        }

        // Always add the bonus add-on product, regardless of match outcome
        if (window.discovery_quiz_bonus_product) {
            recommendations.push({
                name: window.discovery_quiz_bonus_product.title,
                description: window.discovery_quiz_bonus_product.description || 'Add this to complete your set.',
                image_url: window.discovery_quiz_bonus_product.image_url,
                url: window.discovery_quiz_bonus_product.url,
                shop_url: window.discovery_quiz_bonus_product.shop_url,
                isBonus: true
            });
        }

        console.log('Final recommendations:', recommendations);
        return recommendations;
    }

    countMatches(userAnswers, answerKeys) {
        let matches = 0;
        userAnswers.forEach(userAnswer => {
            answerKeys.forEach(key => {
                if (userAnswer.toLowerCase().includes(key.toLowerCase()) || 
                    key.toLowerCase().includes(userAnswer.toLowerCase())) {
                    matches++;
                }
            });
        });
        return matches;
    }

    buildImageUrl(imageUrl, shopUrl) {
        // Handle different image URL formats from Shopify
        if (!imageUrl) return '';
        
        // If it's already a full URL, use it as is
        if (imageUrl.startsWith('http')) {
            return imageUrl;
        }
        
        // If it starts with 'products/' or 'files/', add the CDN prefix
        if (imageUrl.startsWith('products/') || imageUrl.startsWith('files/')) {
            return `${shopUrl}/cdn/shop/${imageUrl}`;
        }
        
        // Default case - assume it's a relative path
        return `${shopUrl}/${imageUrl}`;
    }
}

customElements.define('discovery-quiz-popup', DiscoveryQuizPopup);
